import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import './FlowVisualization.css';

const FlowVisualization = ({flowData, flowName}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const visualizationRef = useRef(null);
  const [viewBox, setViewBox] = useState({x: 0, y: 0, width: 100, height: 100});
  const [isPanning, setIsPanning] = useState(false);
  const [startPoint, setStartPoint] = useState({x: 0, y: 0});
  const [isFullscreen, setIsFullscreen] = useState(false);

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const STATE_WIDTH = 200;
  const STATE_HEIGHT = 100;
  const EVENT_WIDTH = 180;
  const EVENT_HEIGHT = 70;
  const CREATION_WIDTH = 120;
  const CREATION_HEIGHT = 70;

  const ICONS = {
    play: 'M10 8 L10 16 L16 12 Z',
    event: 'M11 10.5C11 9.67 10.33 9 9.5 9S8 9.67 8 10.5C8 11.33 8.67 12 9.5 12S11 11.33 11 10.5ZM17 11.5C17 12.33 16.33 13 15.5 13S14 12.33 14 11.5C14 10.67 14.67 10 15.5 10S17 10.67 17 11.5ZM9.5 15C8.67 15 8 15.67 8 16.5C8 17.33 8.67 18 9.5 18S11 17.33 11 16.5C11 15.67 10.33 15 9.5 15ZM15.5 14C14.67 14 14 14.67 14 15.5C14 16.33 14.67 17 15.5 17S17 16.33 17 15.5C17 14.67 16.33 14 15.5 14Z',
    state: 'M19.43 12.98C19.47 12.65 19.5 12.33 19.5 12S19.47 11.35 19.43 11.02L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.54 5.27C19.41 5.05 19.15 4.96 18.93 5.05L16.56 6.05C16.04 5.65 15.48 5.32 14.87 5.07L14.49 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.51 2.42L9.13 5.07C8.52 5.32 7.96 5.65 7.44 6.05L5.07 5.05C4.85 4.96 4.59 5.05 4.46 5.27L2.34 8.73C2.21 8.95 2.27 9.22 2.46 9.37L4.57 11.02C4.53 11.35 4.5 11.67 4.5 12S4.53 12.65 4.57 12.98L2.46 14.63C2.27 14.78 2.21 15.05 2.34 15.27L4.46 18.73C4.59 18.95 4.85 19.04 5.07 18.95L7.44 17.94C7.96 18.35 8.52 18.68 9.13 18.93L9.51 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.49 21.58L14.87 18.93C15.48 18.68 16.04 18.35 16.56 17.94L18.93 18.95C19.15 19.04 19.41 18.95 19.54 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.98ZM12 15C10.34 15 9 13.66 9 12S10.34 9 12 9S15 10.34 15 12S13.66 15 12 15Z'
  };

  useEffect(() => {
    if (!svgRef.current || !flowData) return;

    const svg = svgRef.current;
    svg.innerHTML = '';

    const createSvgElement = (tag, attributes, parent) => {
      const el = document.createElementNS(SVG_NS, tag);
      for (const key in attributes) {
        el.setAttribute(key, attributes[key]);
      }
      if (parent) {
        parent.appendChild(el);
      }
      return el;
    };

    const createNode = (parent, x, y, width, height, text, nodeType) => {
      const group = createSvgElement('g', {
        class: 'node-group',
        transform: `translate(${x - width / 2}, ${y - height / 2})`
      }, parent);

      let textY = height / 2;
      let iconPath = '';
      let iconX = 10;
      let iconY = (height - 16) / 2;
      let textX = width / 2;
      let textAnchor = 'middle';

      switch (nodeType) {
        case 'start':
          createSvgElement('rect', {width, height, rx: 10, ry: 10, class: 'creation-rect'}, group);
          iconPath = ICONS.play;
          textY = height / 2 + 5;
          textAnchor = 'middle';
          break;
        case 'event':
          createSvgElement('rect', {width, height, rx: 10, ry: 10, class: 'event-rect'}, group);
          iconPath = ICONS.event;
          textX = width / 2;
          textY = height / 2 + 5;
          textAnchor = 'middle';
          break;
        case 'state':
          createSvgElement('rect', {width, height, rx: 10, ry: 10, class: 'state-rect-body'}, group);
          createSvgElement('rect', {width, height: 35, rx: 10, ry: 10, class: 'state-rect-titlebar'}, group);
          createSvgElement('rect', {width, height: 10, y: 25, fill: '#001F38'}, group);
          textY = 22;
          iconPath = ICONS.state;
          iconX = 10;
          iconY = 9.5;
          textX = width / 2;
          textAnchor = 'middle';
          break;
        default:
          // Default case for unknown node types
          createSvgElement('rect', {width, height, rx: 10, ry: 10, class: 'state-rect-body'}, group);
          textAnchor = 'middle';
          break;
      }

      // Create text element with proper wrapping
      const textEl = createSvgElement('text', {
        x: textX,
        y: textY,
        class: 'node-text',
        'text-anchor': textAnchor,
        'dominant-baseline': 'middle'
      }, group);

      // Truncate text if too long
      const maxLength = nodeType === 'state' ? 20 : 18;
      const displayText = text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
      textEl.textContent = displayText;

      if (iconPath && nodeType === 'state') {
        createSvgElement('path', {
          d: iconPath,
          class: 'node-icon',
          transform: `translate(${iconX}, ${iconY})`
        }, group);
      }

      return group;
    };

    const createConnectionLine = (parent, startX, startY, endX, endY) => {
      const pathD = `M ${startX} ${startY} C ${startX + (endX - startX) * 0.4} ${startY}, ${endX - (endX - startX) * 0.4} ${endY}, ${endX} ${endY}`;
      createSvgElement('path', {
        d: pathD,
        class: 'connection-line',
        'marker-end': 'url(#arrowhead)'
      }, parent);
    };

    const getBoundingBox = (nodes) => {
      if (nodes.length === 0) return {minX: 0, minY: 0, maxX: 100, maxY: 100};
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      nodes.forEach(node => {
        const {x, y} = node.pos;
        const width = node.width || 0;
        const height = node.height || 0;
        minX = Math.min(minX, x - width / 2);
        minY = Math.min(minY, y - height / 2);
        maxX = Math.max(maxX, x + width / 2);
        maxY = Math.max(maxY, y + height / 2);
      });
      return {minX, minY, maxX, maxY};
    };

    // Define markers
    const defs = createSvgElement('defs', {}, svg);
    const marker = createSvgElement('marker', {
      id: 'arrowhead',
      viewBox: '0 0 10 10',
      refX: '9',
      refY: '5',
      markerWidth: '6',
      markerHeight: '6',
      orient: 'auto-start-reverse'
    }, defs);
    createSvgElement('path', {d: 'M 0 0 L 10 5 L 0 10 z', class: 'arrow-head'}, marker);

    const gridPattern = createSvgElement('pattern', {
      id: 'dot-grid',
      width: '20',
      height: '20',
      patternUnits: 'userSpaceOnUse'
    }, defs);
    createSvgElement('circle', {cx: '1', cy: '1', r: '1', fill: '#EEEEEE'}, gridPattern);

    // Create main group
    const mainGroup = createSvgElement('g', {id: 'main-group'}, svg);
    createSvgElement('rect', {
      width: '10000',
      height: '10000',
      x: '-5000',
      y: '-5000',
      fill: 'url(#dot-grid)'
    }, mainGroup);

    const linesGroup = createSvgElement('g', {id: 'lines-group'}, mainGroup);
    const nodesGroup = createSvgElement('g', {id: 'nodes-group'}, mainGroup);

    // Layout algorithm
    const allNodes = new Map();
    const allElementsPos = [];
    const connections = [];

    const xStep = 250;
    const yStep = 200;
    let currentX = 100;
    let currentY = 300;

    // Add start node
    const startId = 'start-node';
    const startPos = {x: currentX, y: currentY};
    allNodes.set(startId, {...startPos, type: 'start', name: 'Start', width: CREATION_WIDTH, height: CREATION_HEIGHT});
    allElementsPos.push({pos: startPos, width: CREATION_WIDTH, height: CREATION_HEIGHT});

    // Add creation event
    currentX += xStep;
    const creationEventName = flowData.creationNode?.initialEvent?.name || 'creation-event';
    const creationEventId = 'creation-event';
    const creationEventPos = {x: currentX, y: currentY};
    allNodes.set(creationEventId, {...creationEventPos, type: 'event', name: creationEventName, width: EVENT_WIDTH, height: EVENT_HEIGHT});
    allElementsPos.push({pos: creationEventPos, width: EVENT_WIDTH, height: EVENT_HEIGHT});
    connections.push({from: startId, to: creationEventId});

    // Add initial state
    currentX += xStep;
    const initialStateId = flowData.creationNode?.initialEvent?.targetState;
    if (initialStateId && flowData.states && flowData.states[initialStateId]) {
      const initialState = flowData.states[initialStateId];
      const initialStatePos = {x: currentX, y: currentY};
      allNodes.set(initialStateId, {
        ...initialStatePos,
        type: 'state',
        name: initialState.name || initialStateId,
        width: STATE_WIDTH,
        height: STATE_HEIGHT
      });
      allElementsPos.push({pos: initialStatePos, width: STATE_WIDTH, height: STATE_HEIGHT});
      connections.push({from: creationEventId, to: initialStateId});

      // Process events and connected states
      const queue = [initialStateId];
      const processed = new Set([initialStateId]);

      while (queue.length > 0) {
        const stateId = queue.shift();
        const sourceState = flowData.states[stateId];
        if (!sourceState) continue;

        const sourcePos = allNodes.get(stateId);

        // Find events from this state
        const stateEvents = Object.entries(flowData.events || {}).filter(([, event]) => event.sourceState === stateId);
        const numTransitions = stateEvents.length;
        const baseY = sourcePos.y - ((numTransitions - 1) / 2 * yStep);

        stateEvents.forEach(([eventId, event], i) => {
          const eventX = sourcePos.x + xStep;
          const eventY = baseY + (i * yStep);
          const eventPos = {x: eventX, y: eventY};

          // Add event node
          allNodes.set(eventId, {
            ...eventPos,
            type: 'event',
            name: event.name || 'Event',
            width: EVENT_WIDTH,
            height: EVENT_HEIGHT
          });
          allElementsPos.push({pos: eventPos, width: EVENT_WIDTH, height: EVENT_HEIGHT});
          connections.push({from: stateId, to: eventId});

          // Add target state
          const targetStateId = event.targetState;
          if (targetStateId && flowData.states[targetStateId]) {
            if (!allNodes.has(targetStateId)) {
              const targetX = eventX + xStep;
              const targetY = eventY;
              const targetState = flowData.states[targetStateId];
              const targetPos = {x: targetX, y: targetY};
              allNodes.set(targetStateId, {
                ...targetPos,
                type: 'state',
                name: targetState.name || targetStateId,
                width: STATE_WIDTH,
                height: STATE_HEIGHT
              });
              allElementsPos.push({pos: targetPos, width: STATE_WIDTH, height: STATE_HEIGHT});
            }

            connections.push({from: eventId, to: targetStateId});

            if (!processed.has(targetStateId)) {
              queue.push(targetStateId);
              processed.add(targetStateId);
            }
          }
        });
      }
    }

    // Render lines
    connections.forEach(conn => {
      const fromNode = allNodes.get(conn.from);
      const toNode = allNodes.get(conn.to);
      if (fromNode && toNode) {
        createConnectionLine(linesGroup, fromNode.x, fromNode.y, toNode.x, toNode.y);
      }
    });

    // Render nodes
    allNodes.forEach((node) => {
      createNode(nodesGroup, node.x, node.y, node.width, node.height, node.name, node.type);
    });

    // Set initial viewBox
    const padding = 100;
    const bounds = getBoundingBox(allElementsPos);
    const newViewBox = {
      x: bounds.minX - padding,
      y: bounds.minY - padding,
      width: (bounds.maxX - bounds.minX) + (padding * 2),
      height: (bounds.maxY - bounds.minY) + (padding * 2)
    };
    setViewBox(newViewBox);
    svg.setAttribute('viewBox', `${newViewBox.x} ${newViewBox.y} ${newViewBox.width} ${newViewBox.height}`);
  }, [flowData, ICONS.play, ICONS.event, ICONS.state]);

  // Pan and zoom handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    setStartPoint({x: e.clientX, y: e.clientY});
  };

  const handleMouseMove = (e) => {
    if (!isPanning || !containerRef.current) return;

    const dx = e.clientX - startPoint.x;
    const dy = e.clientY - startPoint.y;

    const newViewBox = {
      x: viewBox.x - dx * (viewBox.width / containerRef.current.clientWidth),
      y: viewBox.y - dy * (viewBox.height / containerRef.current.clientHeight),
      width: viewBox.width,
      height: viewBox.height
    };
    
    if (svgRef.current) {
      svgRef.current.setAttribute('viewBox', `${newViewBox.x} ${newViewBox.y} ${newViewBox.width} ${newViewBox.height}`);
    }
  };

  const handleMouseUp = () => {
    if (!isPanning) return;
    setIsPanning(false);

    if (svgRef.current) {
      const finalViewBox = svgRef.current.getAttribute('viewBox').split(' ').map(Number);
      setViewBox({
        x: finalViewBox[0],
        y: finalViewBox[1],
        width: finalViewBox[2],
        height: finalViewBox[3]
      });
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();

    if (!svgRef.current) return;

    const zoomFactor = 1.1;
    const scale = e.deltaY < 0 ? 1 / zoomFactor : zoomFactor;

    const svgPoint = svgRef.current.createSVGPoint();
    svgPoint.x = e.clientX;
    svgPoint.y = e.clientY;
    const mouseSvg = svgPoint.matrixTransform(svgRef.current.getScreenCTM().inverse());

    const newWidth = viewBox.width * scale;
    const newHeight = viewBox.height * scale;

    const dx = (mouseSvg.x - viewBox.x) * (scale - 1);
    const dy = (mouseSvg.y - viewBox.y) * (scale - 1);

    const newViewBox = {
      x: viewBox.x - dx,
      y: viewBox.y - dy,
      width: newWidth,
      height: newHeight
    };

    setViewBox(newViewBox);
    svgRef.current.setAttribute('viewBox', `${newViewBox.x} ${newViewBox.y} ${newViewBox.width} ${newViewBox.height}`);
  };

  const toggleFullscreen = async () => {
    if (!visualizationRef.current) return;

    try {
      if (!isFullscreen) {
        await visualizationRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fullscreen error:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="flow-visualization-container" ref={visualizationRef}>
      <div className="flow-visualization-header">
        <h3>{flowName || 'Flow Diagram'}</h3>
        <div className="flow-header-actions">
          <p className="flow-hint">{'Click and drag to pan, scroll to zoom'}</p>
          <button
            className="flow-fullscreen-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            type="button"
          >
            {isFullscreen ? '↙' : '↗'}
          </button>
        </div>
      </div>
      <div
        className={`flow-svg-container ${isPanning ? 'panning' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        ref={containerRef}
      >
        <svg height="100%" ref={svgRef} width="100%"></svg>
      </div>
    </div>
  );
};

FlowVisualization.propTypes = {
  flowData: PropTypes.object.isRequired,
  flowName: PropTypes.string
};

export default FlowVisualization;
